// Dashboard em abas: Geral (painel original do Whaticket) e uma aba por fila do usuário,
// com as conversas da fila e os indicadores da área (suporte ou card próprio, ex.: contratação).
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button, Grid, List, ListItem, ListItemText, Paper, Tab, Tabs, Typography, makeStyles, useTheme,
} from "@material-ui/core";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import Dashboard from "../../pages/Dashboard";
import Hoje from "./Hoje";
import suporteApi from "../api";

const useStyles = makeStyles((theme) => ({
  raiz: { flex: 1, overflowY: "auto", padding: theme.spacing(1, 2), ...theme.scrollbarStyles },
  tile: { padding: 14, height: "100%", borderTop: `3px solid ${theme.palette.primary.main}` },
  alerta: { borderTopColor: theme.palette.error.main },
  aviso: { borderTopColor: theme.palette.warning.main },
  ok: { borderTopColor: theme.palette.success.main },
  numero: { fontSize: 30, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" },
  bloco: { padding: 14, height: "100%" },
  secao: { fontWeight: 600, color: theme.palette.text.secondary, fontSize: 13, letterSpacing: 0.4, margin: theme.spacing(2, 0, 1) },
  barra: { height: 8, borderRadius: 4, background: theme.palette.primary.main, minWidth: 2 },
}));

const espera = (min) => (min < 60 ? `${min} min` : min < 1440 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}` : `${Math.floor(min / 1440)}d`);

const Numero = ({ rotulo, valor, detalhe, tom, onClick }) => {
  const c = useStyles();
  return (
    <Paper variant="outlined" className={`${c.tile} ${tom ? c[tom] : ""}`} onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
      <Typography variant="body2" color="textSecondary">{rotulo}</Typography>
      <div className={c.numero}>{valor ?? 0}</div>
      {detalhe && <Typography variant="caption" color="textSecondary">{detalhe}</Typography>}
    </Paper>
  );
};

const Barras = ({ titulo, linhas, rotulo, valor = "total" }) => {
  const c = useStyles();
  const max = Math.max(1, ...linhas.map((l) => l[valor] || 0));
  return (
    <Paper variant="outlined" className={c.bloco}>
      <Typography variant="subtitle2" gutterBottom>{titulo}</Typography>
      {!linhas.length && <Typography variant="body2" color="textSecondary">Nada por aqui.</Typography>}
      {linhas.map((l, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0", fontSize: 13 }}>
          <span style={{ flex: "0 0 45%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l[rotulo]}>{l[rotulo] || "—"}</span>
          <div style={{ flex: 1 }}><div className={c.barra} style={{ width: `${(100 * (l[valor] || 0)) / max}%` }} /></div>
          <b style={{ minWidth: 24, textAlign: "right" }}>{l[valor]}</b>
        </div>
      ))}
    </Paper>
  );
};

const PainelFila = ({ fila }) => {
  const c = useStyles();
  const tema = useTheme();
  const history = useHistory();
  const [d, setD] = useState(null);
  const carregar = useCallback(() => {
    suporteApi.get(`/api/painel/fila/${fila.id}`).then((r) => setD(r.data)).catch(() => {});
  }, [fila.id]);
  useEffect(() => {
    setD(null);
    carregar();
    const t = setInterval(() => !document.hidden && carregar(), 30000);
    return () => clearInterval(t);
  }, [carregar]);
  if (!d) return <Typography color="textSecondary" style={{ padding: 16 }}>Carregando…</Typography>;
  const w = d.whatsapp;
  const a = d.area;
  return (
    <div className={c.raiz}>
      <Typography className={c.secao}>{`CONVERSAS DA FILA ${fila.nome.toUpperCase()}`}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}><Numero rotulo="Em atendimento" valor={w.em_atendimento} onClick={() => history.push("/tickets")} /></Grid>
        <Grid item xs={6} md={3}><Numero rotulo="Aguardando" valor={w.aguardando} tom={w.aguardando > 10 ? "aviso" : undefined}
          detalhe={w.esperando[0] ? `mais antigo há ${espera(w.esperando[0].minutos)}` : undefined} onClick={() => history.push("/tickets")} /></Grid>
        <Grid item xs={6} md={3}><Numero rotulo="Finalizados hoje" valor={w.finalizados_hoje} tom="ok" /></Grid>
        <Grid item xs={6} md={3}><Numero rotulo="Conversas novas hoje" valor={w.hoje} /></Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" className={c.bloco}>
            <Typography variant="subtitle2">Conversas novas por hora (hoje)</Typography>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart data={w.por_hora} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={tema.palette.divider} />
                <XAxis dataKey="hora" tickFormatter={(h) => `${h}h`} tick={{ fontSize: 11, fill: tema.palette.text.secondary }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tema.palette.text.secondary }} axisLine={false} tickLine={false} />
                <Tooltip labelFormatter={(h) => `${h}h`} formatter={(v) => [v, "Conversas"]} cursor={{ fill: tema.palette.action.hover }} />
                <Bar dataKey="total" fill={tema.palette.primary.main} radius={[4, 4, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" className={c.bloco}>
            <Typography variant="subtitle2">Aguardando há mais tempo</Typography>
            {!w.esperando.length && <Typography variant="body2" color="textSecondary">Ninguém esperando. 🎉</Typography>}
            <List dense disablePadding>
              {w.esperando.map((t) => (
                <ListItem key={t.id} button disableGutters onClick={() => history.push(`/tickets/${t.id}`)}>
                  <ListItemText primary={t.contato} secondary={`há ${espera(t.minutos)}${t.naolidas ? ` · ${t.naolidas} não lida(s)` : ""}`}
                    primaryTypographyProps={{ noWrap: true, variant: "body2" }} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}><Barras titulo="Em atendimento por atendente" linhas={w.atendentes} rotulo="atendente" valor="abertos" /></Grid>
      </Grid>

      {a.tipo === "suporte" ? (
        <>
          <Typography className={c.secao}>SUPORTE TÉCNICO</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}><Numero rotulo="Cards no quadro" valor={a.abertos} onClick={() => history.push("/suporte/quadro")} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Atrasados (+4h)" valor={a.atrasados} tom={a.atrasados ? "alerta" : "ok"} onClick={() => history.push("/suporte/quadro")} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Finalizados hoje" valor={a.finalizados_hoje} tom="ok" /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Protocolos hoje" valor={a.protocolos_hoje} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Formulários para validar" valor={a.para_validar} tom={a.para_validar ? "aviso" : undefined} onClick={() => history.push("/suporte/formularios")} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Pendentes (histórico)" valor={a.pendentes} onClick={() => history.push("/suporte/historico")} /></Grid>
            <Grid item xs={12} md={6}><Barras titulo="Cards abertos por analista" linhas={a.por_analista} rotulo="analista" valor="abertos" /></Grid>
          </Grid>
        </>
      ) : (
        <>
          <Typography className={c.secao}>{`${(a.nome || "").toUpperCase()}S`}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}><Numero rotulo="Em andamento" valor={a.em_andamento} onClick={() => history.push("/suporte/cards-fila")} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Novos hoje" valor={a.novos_hoje} detalhe={`${a.novos_7_dias} nos últimos 7 dias`} /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Concluídos (30 dias)" valor={a.concluidos_30_dias} tom="ok" /></Grid>
            <Grid item xs={6} md={2}><Numero rotulo="Formulários para validar" valor={a.para_validar} tom={a.para_validar ? "aviso" : undefined} onClick={() => history.push("/suporte/formularios")} /></Grid>
            {a.documentos && <Grid item xs={6} md={2}><Numero rotulo="Documentos vencidos" valor={a.documentos.vencidos} tom={a.documentos.vencidos ? "alerta" : "ok"} onClick={() => history.push("/suporte/documentos")} /></Grid>}
            {a.documentos && <Grid item xs={6} md={2}><Numero rotulo="Vencem em 30 dias" valor={a.documentos.vence_30} tom={a.documentos.vence_30 ? "aviso" : "ok"} onClick={() => history.push("/suporte/documentos")} /></Grid>}
            <Grid item xs={12} md={7}><Barras titulo="Em andamento por etapa" linhas={a.etapas} rotulo="etapa" /></Grid>
            <Grid item xs={12} md={5}><Barras titulo="Resultados (últimos 30 dias)" linhas={a.resultados_30_dias} rotulo="resultado" /></Grid>
          </Grid>
          <div style={{ marginTop: 12 }}>
            <Button variant="outlined" color="primary" onClick={() => history.push("/suporte/cards-fila")}>{`Abrir ${(a.nome || "cards").toLowerCase()}s`}</Button>
          </div>
        </>
      )}
    </div>
  );
};

const Painel = () => {
  const [filas, setFilas] = useState([]);
  const [aba, setAba] = useState(() => {
    try { return localStorage.getItem("painel:aba") || "geral"; } catch { return "geral"; }
  });
  useEffect(() => {
    suporteApi.get("/api/painel/filas").then((r) => setFilas(r.data)).catch(() => {});
  }, []);
  const escolher = (v) => {
    setAba(v);
    try { localStorage.setItem("painel:aba", v); } catch { /* sem armazenamento */ }
  };
  const fila = filas.find((q) => String(q.id) === String(aba));
  // quem é só de filas com card próprio (ex.: Contratação) continua com o painel geral de conversas
  const soContratacao = filas.length > 0 && filas.every((q) => q.card_proprio);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <Tabs value={fila ? String(fila.id) : "geral"} onChange={(e, v) => escolher(v)} indicatorColor="primary" textColor="primary"
        variant="scrollable" scrollButtons="auto" style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Tab value="geral" label="Hoje" />
        {filas.map((q) => <Tab key={q.id} value={String(q.id)} label={q.nome} />)}
      </Tabs>
      {fila ? <PainelFila key={fila.id} fila={fila} /> : soContratacao ? <div style={{ flex: 1, overflowY: "auto" }}><Dashboard /></div> : <Hoje />}
    </div>
  );
};

export default Painel;
